/**
 * DestructiveConfirmDialog — typed-confirmation modal for destructive
 * Iter 47 actions (confirm / execute / rollback / out-of-band restore).
 *
 * Operator must literally type the phrase "RESTORE PRODUCTION" (or a
 * caller-overridable phrase). The CONFIRM button stays disabled until the
 * typed value matches exactly. Closing the modal (ESC / outside-click)
 * does NOT trigger the destructive action.
 *
 * This is the UI half of the destructive guard. The token header is
 * supplied separately by the parent (session-scoped state, never
 * persisted to localStorage).
 */
import { useEffect, useState } from "react";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
    DialogFooter,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Warning } from "@phosphor-icons/react";


export default function DestructiveConfirmDialog({
    open,
    onOpenChange,
    title,
    description,
    expectedPhrase = "RESTORE PRODUCTION",
    confirmLabel = "Run destructive action",
    confirmingLabel = "Working…",
    confirming = false,
    onConfirm,
    extraInfo,           // optional ReactNode rendered above the input
    testId,
}) {
    const [typed, setTyped] = useState("");

    // Reset typed value whenever the modal opens / closes
    useEffect(() => {
        if (!open) setTyped("");
    }, [open]);

    const matches = typed === expectedPhrase;

    return (
        <Dialog open={open} onOpenChange={(o) => !confirming && onOpenChange(o)}>
            <DialogContent data-testid={testId || "destructive-confirm-dialog"}>
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-rose-700">
                        <Warning size={20} weight="fill" />
                        {title}
                    </DialogTitle>
                    {description && (
                        <DialogDescription>{description}</DialogDescription>
                    )}
                </DialogHeader>

                {extraInfo}

                <div className="space-y-2 mt-2">
                    <label className="block text-xs font-semibold text-slate-700">
                        Type the phrase below to confirm:
                    </label>
                    <div className="text-xs font-mono px-2 py-1 rounded bg-slate-100 text-slate-900 inline-block">
                        {expectedPhrase}
                    </div>
                    <Input
                        autoFocus
                        value={typed}
                        onChange={(e) => setTyped(e.target.value)}
                        placeholder="Type the exact phrase to enable confirm"
                        className="font-mono"
                        data-testid="destructive-confirm-phrase-input"
                        disabled={confirming}
                    />
                </div>

                <DialogFooter className="mt-4 gap-2">
                    <Button
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        disabled={confirming}
                        data-testid="destructive-confirm-cancel-btn"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={onConfirm}
                        disabled={!matches || confirming}
                        className="bg-rose-600 hover:bg-rose-500 text-white"
                        data-testid="destructive-confirm-action-btn"
                    >
                        {confirming ? confirmingLabel : confirmLabel}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
